import type { EmailOverviewSortKey } from "@/types/email-analytics";

export const UNKNOWN_EMAIL_TYPE = "unknown";

export const EMAIL_TYPES = {
  welcome: "welcome",
  careplanApproved: "careplan_approved",
  careplanRequiresReview: "careplan_requires_review",
  updatedCareplanRequiresReview: "updated_careplan_requires_review",
  updatedCareplanApproved: "updated_careplan_approved",
  doseOrNonSubscribedChanges: "dose_or_non_subscribed_changes",
  doseOrNonSubscribedChangesAfterClinicianReview:
    "dose_or_non_subscribed_changes_after_clinician_review",
  subscribedProductRemoved: "subscribed_product_removed",
  subscribedProductRemovedAfterClinicianReview: "subscribed_product_removed_after_clinician_review",
  subscribedProductsReplaced: "subscribed_products_replaced",
  subscribedProductsReplacedAfterClinicianReview:
    "subscribed_products_replaced_after_clinician_review",
  noChangesAfterClinicianReview: "no_changes_after_clinician_review",
  driveToSurvey: "drive_to_survey",
  driveToSurveyReminder: "drive_to_survey_reminder",
  howIsPlanWorking: "how_is_plan_working",
  hereToHelp: "here_to_help",
  firstOrderProductsReminder: "first_order_products_reminder",
  secondOrderProductsReminder: "second_order_products_reminder",
  thirdOrderProductsReminder: "third_order_products_reminder",
  resumeProfilingFirst: "resume_profiling_first",
  resumeProfilingSecond: "resume_profiling_second",
  discontinueProduct: "discontinue_product",
  threadMessage: "thread_message",
  sendOtp: "send_otp",
  resetPassword: "reset_password",
  personifyPasswordReset: "personify_password_reset",
  sendEmailPasswordChanged: "send_email_password_changed",
  unknown: UNKNOWN_EMAIL_TYPE,
} as const;

export const EMAIL_TYPE_LABELS: Record<string, string> = {
  [EMAIL_TYPES.welcome]: "Welcome — plan auto-approved",
  [EMAIL_TYPES.careplanApproved]: "Welcome — plan approved after review",
  [EMAIL_TYPES.careplanRequiresReview]: "Care plan under clinical review",
  [EMAIL_TYPES.updatedCareplanRequiresReview]: "Updated care plan under clinical review",
  [EMAIL_TYPES.updatedCareplanApproved]: "Updated care plan approved",
  [EMAIL_TYPES.doseOrNonSubscribedChanges]: "Plan update — dose or non-subscription product change",
  [EMAIL_TYPES.doseOrNonSubscribedChangesAfterClinicianReview]:
    "Plan update after review — dose or non-subscription product change",
  [EMAIL_TYPES.subscribedProductRemoved]: "Plan update — subscribed product removed",
  [EMAIL_TYPES.subscribedProductRemovedAfterClinicianReview]:
    "Plan update after review — subscribed product removed",
  [EMAIL_TYPES.subscribedProductsReplaced]: "Plan update — subscribed product replaced",
  [EMAIL_TYPES.subscribedProductsReplacedAfterClinicianReview]:
    "Plan update after review — subscribed product replaced",
  [EMAIL_TYPES.noChangesAfterClinicianReview]: "Check-in reviewed — no changes",
  [EMAIL_TYPES.driveToSurvey]: "Check-in invitation",
  [EMAIL_TYPES.driveToSurveyReminder]: "Check-in reminder",
  [EMAIL_TYPES.howIsPlanWorking]: '"Is your plan working?" follow-up',
  [EMAIL_TYPES.hereToHelp]: "Products delivered",
  [EMAIL_TYPES.firstOrderProductsReminder]: "Order reminder 1 of 3",
  [EMAIL_TYPES.secondOrderProductsReminder]: "Order reminder 2 of 3",
  [EMAIL_TYPES.thirdOrderProductsReminder]: "Order reminder 3 of 3 (final)",
  [EMAIL_TYPES.resumeProfilingFirst]: "Finish-profile reminder 1 of 2",
  [EMAIL_TYPES.resumeProfilingSecond]: "Finish-profile reminder 2 of 2",
  [EMAIL_TYPES.discontinueProduct]: "Product discontinued",
  [EMAIL_TYPES.threadMessage]: "New message from the care team",
  [EMAIL_TYPES.sendOtp]: "Verification code",
  [EMAIL_TYPES.resetPassword]: "Password reset",
  [EMAIL_TYPES.personifyPasswordReset]: "Password reset (Personify)",
  [EMAIL_TYPES.sendEmailPasswordChanged]: "Password changed confirmation",
  [EMAIL_TYPES.unknown]: "Unknown email",
};

export const EMAIL_DELIVERY_ISSUES = {
  rejection: "rejection",
  hardBounce: "hard_bounce",
  softBounce: "soft_bounce",
  spamReport: "spam_report",
} as const;

export const EMAIL_DELIVERY_ISSUE_LABELS = {
  rejection: "Rejected",
  hard_bounce: "Hard bounce",
  soft_bounce: "Soft bounce",
  spam_report: "Spam report",
} as const;

export const EMAIL_DELIVERY_ISSUES_PAGE_SIZE_OPTIONS = [25, 50, 75, 100] as const;

export const DEFAULT_EMAIL_DELIVERY_ISSUES_PAGE_SIZE = 25;

export const EMAIL_TREND_METRICS = {
  openRate: { label: "Open rate", kind: "rate", higherIsBetter: true },
  clickRate: { label: "Click rate", kind: "rate", higherIsBetter: true },
  deliveryRate: { label: "Delivery rate", kind: "rate", higherIsBetter: true },
  bounceRate: { label: "Bounce rate", kind: "rate", higherIsBetter: false },
  spamReports: { label: "Spam reports", kind: "count", higherIsBetter: false },
  sent: { label: "Emails sent", kind: "count", higherIsBetter: true },
} as const;

export const EMAIL_TREND_RANGE_PRESETS = {
  last3MonthsWeekly: { label: "Last 3 months", months: 3, groupBy: "week" },
  last6MonthsMonthly: { label: "Last 6 months", months: 6, groupBy: "month" },
  last12MonthsMonthly: { label: "Last 12 months", months: 12, groupBy: "month" },
} as const;

export const RATE_AXIS_STEPS = [1, 2, 5, 10, 20, 25, 50] as const;

export const RATE_AXIS_INTEGER_SPAN = 4;

export const DEFAULT_EMAIL_TREND_RANGE_PRESET = "last6MonthsMonthly";

export const DEFAULT_EMAIL_OVERVIEW_SORT = "-sent";

export const EMAIL_OVERVIEW_SORT_KEYS = [
  "emailType",
  "sent",
  "delivered",
  "openRate",
  "clickRate",
  "bounceRate",
  "spamReports",
] as const satisfies readonly EmailOverviewSortKey[];

export const EMAIL_OVERVIEW_RANGE_PRESETS = {
  last30Days: { label: "Last 30 days", days: 30 },
  last90Days: { label: "Last 90 days", days: 90 },
  thisYear: { label: "This year" },
} as const;

export const DEFAULT_EMAIL_OVERVIEW_RANGE_PRESET = "last30Days";

export const EMAIL_WINDOW_DAYS = 30;

export const API_DATE_FORMAT = "yyyy-MM-dd";

const UTC_OFFSET_FORMAT = "xxx";

export const API_DATE_TIME_FORMAT = `yyyy-MM-dd'T'HH:mm:ss${UTC_OFFSET_FORMAT}`;

export const END_OF_DAY = { hours: 23, minutes: 59, seconds: 59 } as const;

export const EMAIL_SEARCH_DEBOUNCE_MS = 300;

export const EMAIL_OVERVIEW_ROWS_PER_PAGE = 8;

export const EMAIL_TREND_FLAT_THRESHOLD = 0.05;

export const EMAIL_TREND_CHART_HEIGHT_CLASS = "h-75";

export const EMAIL_TREND_CHART = {
  axisFontSize: 12,
  axisWidth: 56,
  margin: { bottom: 0, left: 0, right: 16, top: 8 },
  strokeWidth: 2,
  dashArray: "5 4",
  areaOpacity: 0.12,
  dotRadius: 3,
  activeDotRadius: 5,
  maxBarSize: 40,
  barRadius: [4, 4, 0, 0] as [number, number, number, number],
  currentBarOpacity: 0.35,
} as const;
